/* `/mcp` — the workspace's MCP servers, their agent links, and OAuth sign-in.
   backend/modules/mcp/README.md for the wire. Only the agents feature reads it, so it lives
   in this feature, not `shared/api` (rule book §1 rule 3).

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes
   through `unwrap`. */
import { client } from "@/shared/api/client";
import { unwrap, type Envelope } from "@/shared/api/envelope";

import type {
  McpLink,
  McpOAuthStart,
  McpOAuthStarted,
  McpServer,
  McpServerCreate,
  McpServerPatch,
  McpTestBody,
  McpTestResult,
} from "@/shared/types";

export const mcpKeys = {
  all: ["mcp"] as const,
  servers: (agentId?: string) => [...mcpKeys.all, "servers", agentId ?? null] as const,
  server: (id: string) => [...mcpKeys.all, "server", id] as const,
};

/** The page the sign-in returns to. It must be on an origin the API allows. */
export const OAUTH_CALLBACK_PATH = "/oauth/mcp/callback";
/** How the callback page reaches the dialog that opened it — survives the
    sign-in page cutting `window.opener` (Cross-Origin-Opener-Policy). */
export const OAUTH_CHANNEL = "mcp-oauth";

// A probe, maybe a sign-in discovery, then the save: the API allows 30 s for these.
const SLOW = { timeout: 35_000 };

/** With `agentId`, only the servers mounted on that agent, each with `link`. */
export async function listServers(agentId?: string): Promise<McpServer[]> {
  return unwrap(await client.get<Envelope<McpServer[]>>("/mcp/servers", { params: { agent_id: agentId } }));
}

export async function getServer(id: string): Promise<McpServer> {
  return unwrap(await client.get<Envelope<McpServer>>(`/mcp/servers/${id}`));
}

/** Connects, initializes and lists tools. Saves nothing. */
export async function testServer(body: McpTestBody): Promise<McpTestResult> {
  return unwrap(await client.post<Envelope<McpTestResult>>("/mcp/servers/test", body, SLOW));
}

export async function createServer(body: McpServerCreate): Promise<McpServer> {
  return unwrap(await client.post<Envelope<McpServer>>("/mcp/servers", body, SLOW));
}

export async function patchServer(id: string, body: McpServerPatch): Promise<McpServer> {
  return unwrap(await client.patch<Envelope<McpServer>>(`/mcp/servers/${id}`, body, SLOW));
}

export async function refreshServer(id: string): Promise<McpServer> {
  return unwrap(await client.post<Envelope<McpServer>>(`/mcp/servers/${id}/refresh`, undefined, SLOW));
}

export async function deleteServer(id: string): Promise<void> {
  await client.delete(`/mcp/servers/${id}`);
}

export async function linkServer(id: string, agentId: string): Promise<McpLink> {
  return unwrap(await client.post<Envelope<McpLink>>(`/mcp/servers/${id}/agents`, { agent_id: agentId }));
}

export async function setLinkEnabled(id: string, agentId: string, enabled: boolean): Promise<McpLink> {
  return unwrap(await client.patch<Envelope<McpLink>>(`/mcp/servers/${id}/agents/${agentId}`, { enabled }));
}

export async function unlinkServer(id: string, agentId: string): Promise<void> {
  await client.delete(`/mcp/servers/${id}/agents/${agentId}`);
}

export async function startOAuth(body: McpOAuthStart): Promise<McpOAuthStarted> {
  return unwrap(await client.post<Envelope<McpOAuthStarted>>("/mcp/oauth", body, SLOW));
}

/** The authorization code never enters a query cache or browser storage. */
export async function completeOAuth(state: string, code: string): Promise<McpServer> {
  return unwrap(await client.post<Envelope<McpServer>>("/mcp/oauth/complete", { state, code }, SLOW));
}
